import React, { useState, useEffect } from "react";
import { shorthands } from "@griffel/react";
import {
  Label,
  Button,
  Textarea,
  Combobox,
  Option,
  makeStyles,
  TabList,
  Tab,
  Tooltip,
  Link,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Checkbox,
  Spinner,
  mergeClasses,
} from "@fluentui/react-components";
import { SendButtonTooltip } from "./SendButtonTooltip";
import { PropertyOption } from "./PropertyOption";
import PropTypes from "prop-types";
import {
  ClipboardTextEditRegular,
  ArrowReplyRegular,
  ArrowReplyAllRegular,
  ChatRegular,
  ChatFilled,
  SendRegular,
  MailRegular,
  ArrowReplyFilled,
  ArrowReplyAllFilled,
  MailFilled,
  ChatSparkleRegular,
  InfoRegular,
  bundleIcon,
  SendFilled,
  ClipboardTextEditFilled,
  ChatSparkleFilled,
  DocumentArrowUpFilled,
  DocumentArrowUpRegular,
  QuestionCircleFilled,
  QuestionCircleRegular,
  SettingsFilled,
  SettingsRegular,
} from "@fluentui/react-icons";
import UnicliTooltip from "./UnicliTooltip";
import ContextBox from "./ContextBox";
import FileContextPicker from "./FileContextPicker";
import SelectedFileChips from "./SelectedFileChips";
import "./ComposeBox.css";

const useStyles = makeStyles({
  composeBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    ...shorthands.padding("0px", "0px", "0px"),
    ...shorthands.gap("5px"),
    isolation: "isolate",
    minHeight: "207.5px",
    height: "auto",
    ...shorthands.flex("none"),
    order: 1,
    alignSelf: "stretch",
    flexGrow: 0,
    // Ensure all focused descendants use theme outline color
    "& *:focus": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0px",
      boxShadow: "none",
    },
    "& *:focus-visible": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0px",
      boxShadow: "none",
    },
    "& *:focus-within": {
      outlineColor: "#357C7C",
    },
  },
  composeBoxWord: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    ...shorthands.padding("0px", "0px", "0px"),
    ...shorthands.gap("5px"),
    isolation: "isolate",
    //minWidth: "280px",
    //width: "300px",
    minHeight: "197.5px",
    height: "auto",
    ...shorthands.flex("none"),
    order: 1,
    alignSelf: "stretch",
    flexGrow: 0,
    // Ensure all focused descendants use theme outline color
    "& *:focus": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0px",
      boxShadow: "none",
    },
    "& *:focus-visible": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0px",
      boxShadow: "none",
    },
    "& *:focus-within": {
      outlineColor: "#357C7C",
    },
  },
  contextTabBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.padding("0px"),
    // ...shorthands.gap("4px"),
    //minWidth: "300px",
    height: "36.5px",
    ...shorthands.flex("none"),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
    zIndex: 1,
    //maxWidth: "284px",
    //...shorthands.flex("none"),
  },
  contextTabBarWord: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.padding("0px"),
    // ...shorthands.gap("4px"),
    //minWidth: "284px",
    height: "36.5px",
    ...shorthands.flex("none"),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
    zIndex: 1,
    //maxWidth: "284px",
    //...shorthands.flex("none"),
  },
  fileResourceLoadingLine: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.gap("8px"),
    alignSelf: "stretch",
    fontFamily: "Segoe UI",
    fontSize: "13px",
    lineHeight: "18px",
    color: "#333333",
  },
  dropdownMenu: {
    ...shorthands.padding("0px"),
    ...shorthands.gap("3px"),
    ...shorthands.border("1px", "solid", "#357C7C"),
    //minWidth: "180px",
    height: "36.5px",
    alignSelf: "stretch",
    "--colorCompoundBrandStroke": "#00000000",
    "--colorCompoundBrandStrokeHover": "#00000000",
    "--colorCompoundBrandStrokePressed": "#00000000",
  },
  dropdownMenuCompose: {
    ...shorthands.padding("0px"),
    ...shorthands.gap("3px"),
    ...shorthands.border("1px", "solid", "#357C7C"),
    //minWidth: "224px",
    minWidth: "160px",
    height: "36.5px",
    ...shorthands.flex("none"),
    alignSelf: "stretch",
    "--colorCompoundBrandStroke": "#00000000",
    "--colorCompoundBrandStrokeHover": "#00000000",
    "--colorCompoundBrandStrokePressed": "#00000000",
  },
  dropdownMenuListbox: {
    ...shorthands.border("1px", "solid", "#357C7C"),
  },
  respondChatTabs: {
    display: "flex",
    flexDirection: "row",
    alignItems: "end",
    marginLeft: "6px",
    ...shorthands.gap("3px"),
    ...shorthands.padding("0px"),
    width: "81px",
    height: "36px",
    ...shorthands.flex("none"),
    order: 2,
    flexGrow: 0,
  },
  respondChatTabsCompose: {
    display: "flex",
    flexDirection: "row",
    alignItems: "end",
    marginLeft: "6px",
    ...shorthands.gap("3px"),
    ...shorthands.padding("0px"),
    width: "54px",
    height: "36px",
    ...shorthands.flex("none"),
    order: 2,
    flexGrow: 0,
  },
  iconButton: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    alignItems: "center",
    ...shorthands.padding("0px"),
    width: "26px",
    height: "36px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.flex("none"),
    order: 1,
    flexGrow: 0,
    outline: "none",
    ":focus": {
      outline: "1px solid #357C7C",
      outlineOffset: "0px",
    },
    ":focus-visible": {
      outline: "1px solid #357C7C",
      outlineOffset: "0px",
    },
    ":focus-within": {
      outline: "1px solid #357C7C",
      outlineOffset: "0px",
    },
    "&::after": {
      "--fui-Tab__indicator--offset": "-8px",
      "--fui-Tab__indicator--scale": "10",
      "background-color": "#357C7C",
      height: "1px",
      "transform-origin": "left center",
    },
  },
  buttonLabel: {
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "6.61765px",
    lineHeight: "9px", // 140% of the font size
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    color: "#666666",
    ...shorthands.flex("none"),
    order: 1,
    flexGrow: 0,
    whiteSpace: "nowrap",
  },
  inputArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.padding("0px"),
    ...shorthands.gap("2px"),
    isolation: "isolate",
    //minWidth: "287px",
    minHeight: "133px",
    height: "auto",
    ...shorthands.flex("none"),
    order: 1,
    alignSelf: "stretch",
    flexGrow: 0,
    zIndex: 0,
  },
  inputFluent2: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.padding("0px"),
    isolation: "isolate",
    //minWidth: "284px",
    minHeight: "133px",
    height: "auto",
    ...shorthands.borderRadius("4px"),
    ...shorthands.flex("none"),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
    zIndex: 0,
    ...shorthands.overflow("hidden"),
  },
  contents: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    //...shorthands.padding("0px", "2px"),
    //...shorthands.gap("10px"),
    //minWidth: "284px",
    minHeight: "133px",
    height: "auto",
    backgroundColor: "#FFFFFF",
    ...shorthands.border("1px", "solid", "#357C7C"),
    ...shorthands.borderRadius("4px"),
    ...shorthands.flex("none"),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 1,
    zIndex: 0,
  },
  placeholderText: {
    //minWidth: "280px",
    minHeight: "80px",
    height: "auto",
    ...shorthands.padding("0px"),
    ...shorthands.flex("none"),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 1,
    ...shorthands.border("none"),
    boxShadow: "none", // Removes any box shadow that might appear as a border
    ...shorthands.outline("none"), // Removes the outline when the element is focused
    "&:focus": {
      ...shorthands.border("none"),
      boxShadow: "none",
    },
    "&:hover": {
      ...shorthands.border("none"),
      boxShadow: "none",
    },
    ":focus-within": {
      ...shorthands.border("none"),
      boxShadow: "none",
    },
    ":active": {
      ...shorthands.border("none"),
      boxShadow: "none",
    },
    overflowY: "hidden",
    overflowX: "hidden",
    "--colorCompoundBrandStroke": "#00000000",
    "--colorCompoundBrandStrokeHover": "#00000000",
    "--colorCompoundBrandStrokePressed": "#00000000",
  },
  placeholderTextInput: {
    maxHeight: "40vh !important",
    resize: "none !important",
    overflowY: "auto !important",
    boxSizing: "border-box !important",
  },
  actionFrame: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    ...shorthands.padding("4px"),
    ...shorthands.gap("2px"),
    isolation: "isolate",
    //minWidth: "276px",
    height: "34px",
    ...shorthands.flex("none"),
    order: 1,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  divider: {
    width: "1px",
    height: "20px",
    backgroundColor: "#357C7C",
    ...shorthands.flex("none"),
    order: 1,
    //alignSelf: "stretch",
    flexGrow: 0,
    zIndex: 1,
  },
  newTopic: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "auto",
    ...shorthands.padding("4px", "8px"),
    ...shorthands.borderRadius("4px"),
    ...shorthands.flex("none"),
    order: 0,
    flexGrow: 0,
    ...shorthands.border("0px", "solid", "#357C7C"),
    columnGap: "2px",
    boxShadow: "none",
    outline: "none",
    ":focus": {
      ...shorthands.border("0px", "solid", "#357C7C"),
      outline: "none",
      boxShadow: "none",
    },
    ":focus-visible": {
      ...shorthands.border("2px", "solid", "#357C7C"),
      outline: "none",
      boxShadow: "none",
    },
    ":focus-within": {
      ...shorthands.border("2px", "solid", "#357C7C"),
      outline: "none",
      boxShadow: "none",
    },
    ":hover": {
      ...shorthands.border("0px", "solid", "#357C7C"),
    },
    ":active": {
      ...shorthands.border("0px", "solid", "#357C7C"),
    },
  },
  handleHelp: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "end",
    alignItems: "end",
    ...shorthands.padding("0px"),
    width: "20px",
    height: "20px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.flex("none"),
    order: 0,
    flexGrow: 0,
    ...shorthands.border("none"),
    boxShadow: "none", // Removes any box shadow that might appear as a border
  },
  handleSettings: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "end",
    alignItems: "end",
    ...shorthands.padding("0px"),
    width: "20px",
    height: "20px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.flex("none"),
    order: 0,
    flexGrow: 0,
    ...shorthands.border("none"),
    outline: "none",
    boxShadow: "none",
    ":focus": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0",
      boxShadow: "none",
    },
    ":focus-visible": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0",
      boxShadow: "none",
    },
    ":focus-within": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0",
      boxShadow: "none",
    },
  },
  submitButtom: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding("4px", "8px"),
    ...shorthands.borderRadius("4px"),
    ...shorthands.flex("none"),
    order: 2,
    flexGrow: 0,
    ...shorthands.border("1px", "solid", "#357C7C"),
    columnGap: "2px",
    boxShadow: "none",
    outline: "none",
    ":focus": {
      ...shorthands.border("2px", "solid", "#357C7C"),
      outline: "none",
      boxShadow: "none",
    },
    ":focus-visible": {
      ...shorthands.border("2px", "solid", "#357C7C"),
      outline: "none",
      boxShadow: "none",
    },
    ":focus-within": {
      ...shorthands.border("2px", "solid", "#357C7C"),
      outline: "none",
      boxShadow: "none",
    },
    ":hover": {
      ...shorthands.border("1px", "solid", "#357C7C"),
    },
    ":active": {
      ...shorthands.border("1px", "solid", "#357C7C"),
    },
  },
  buttonText: {
    fontSize: "12px",
    color: "#357C7C",
    whiteSpace: "nowrap",
  },
  comboBoxInput: {
    "&:focus::after": {
      borderBottomColor: "#357C7C", // Change underline color on focus
    },
  },
  disclaimer: {
    display: "flex",
    flexDirection: "row",
    ...shorthands.padding("2px"),
    //...shorthands.gap("4px"),
    //minWidth: "264px",
    width: "100%",
    height: "auto",
    backgroundColor: "#F5F5F5",
    ...shorthands.borderRadius("4px"),
    ...shorthands.flex("none"),
    order: 4,
    alignSelf: "stretch",
    alignItems: "center",
    flexGrow: 0,
    zIndex: 3,
    justifyContent: "space-between",
  },
  infoIcon: {
    alignSelf: "center",
    width: "12px",
    height: "12px",
    flex: "none",
    order: 0,
    flexGrow: 0,
    marginRight: "4px",
  },
  disclaimerText: {
    //minWidth: "149px",
    height: "10px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "10px",
    lineHeight: "9px", // 93% of the box height
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    textAlign: "center",
    color: "#808080",
    flex: "none",
    order: 1,
    flexGrow: 1,
    marginRight: "4px",
  },
  signoutLink: {
    width: "91px",
    height: "10px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "10px",
    lineHeight: "9px", // identical to box height, or 93%
    display: "flex",
    alignItems: "end",
    alignSelf: "flex-end",
    textAlign: "right",
    color: "#0F6CBD",
    order: 2,
    flex: "none",
    justifyContent: "flex-end",
    marginLeft: "auto",
    marginRight: "4px",
  },
  toneSelector: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    alignContent: "flex-start",
    ...shorthands.gap("4px", "8px"),
    width: "100%",
    minHeight: "14px",
    height: "auto",
    ...shorthands.padding("0px"),
    order: 2,
    alignSelf: "stretch",
    flexGrow: 0,
    zIndex: 0,
    marginRight: "0px",
    position: "relative",
  },
  toneStyleInline: {
    display: "inline-flex",
    alignItems: "center",
    height: "14px",
    flexShrink: 0,
  },
  searchOptionsAnchor: {
    display: "flex",
    flex: "1 1 0",
    justifyContent: "flex-end",
    alignItems: "center",
    minWidth: "min-content",
    ...shorthands.margin("0px"),
    ...shorthands.padding("0px"),
  },
  searchOptionsGroup: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
    ...shorthands.gap("2px"),
    flexShrink: 0,
    ...shorthands.margin("0px"),
    ...shorthands.padding("0px"),
  },
  toneMenu: {
    display: "flex",
    flexDirection: "row",
    //alignItems: "center",
    padding: "0px 5px",
    gap: "5px",
    position: "absolute",
    height: "14px",
    left: "0px",
    top: "0px",
  },
  popupMenu: {
    display: "flex",
    width: "65px",
    padding: "5px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    position: "absolute",
    left: 0,
    top: "-60px",
    borderRadius: "4px",
    border: "0.2px solid var(--themePrimary, #357C7C)",
    background: "#FFF",
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.25)",
    zIndex: 10,
  },
  popupAnchor: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    height: "14px",
  },
  popupItem: {
    height: "14px",
    fontFamily: "'Segoe UI', sans-serif",
    fontWeight: 400,
    fontSize: "10px",
    lineHeight: "14px",
    color: "#357C7C",
    cursor: "pointer",
    "&:hover": {
      fontWeight: "bold",
    },
  },
  toneDivider: {
    width: "1px",
    height: "14px",
    backgroundColor: "#357C7C",
    ...shorthands.flex("none"),
    //order: 1,
    //alignSelf: "stretch",
    flexGrow: 0,
    zIndex: 1,
  },
  toneLabel: {
    height: "14px",
    fontFamily: "'Segoe UI', sans-serif",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "10px",
    lineHeight: "14px",
    color: "#4D4D4D",
    flex: "none",
    order: 0,
    flexGrow: 0,
  },
  searchDocsLabel: {
    fontFamily: "'Segoe UI', sans-serif",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "10px",
    lineHeight: "14px",
    color: "#4D4D4D",
    flex: "none",
    order: 0,
    flexGrow: 0,
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "center",
    ...shorthands.margin("0px"),
    ...shorthands.padding("0px", "3px", "0px", "6px"),
  },
  searchDocsDiv: {
    width: "auto",
    display: "flex",
    ...shorthands.padding("0px"),
    ...shorthands.margin("0px"),
    ...shorthands.gap("1px"),
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    whiteSpace: "nowrap",
    order: 1,
    flexGrow: 0,
    zIndex: 0,
  },
  searchDocsSwitch: {
    height: "14px",
  },
  searchDocsCheckbox: {
    '--colorCompoundBrandBackground': '#357C7C',
    '--colorCompoundBrandStroke': '#357C7C',
    '--colorCompoundBrandBackgroundHover': '#357C7C',
    // Force Fluent focus stroke tokens to our theme color
    '--colorStrokeFocus1': '#357C7C',
    '--colorStrokeFocus2': '#357C7C',
    '--colorStrokeFocus': '#357C7C',
    display: "inline-flex",
    alignItems: "center",
    alignSelf: "center",
    ...shorthands.margin("0px"),
    ...shorthands.padding("0px"),
    outline: "none",
    ":focus": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0",
      boxShadow: "none",
    },
    ":focus-visible": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0",
      boxShadow: "none",
    },
    ":focus-within": {
      outline: "1px solid #357C7C !important",
      outlineOffset: "0",
      boxShadow: "none",
    },
  },
  toneMenuItem: {
    height: "14px",
    fontFamily: "'Segoe UI', sans-serif",
    //fontStyle: "normal",
    fontWeight: 400,
    fontSize: "10px",
    lineHeight: "14px",
    color: "#357C7C",
    flex: "none",
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    "&:hover": {
      fontWeight: "bold",
    },
    order: 0,
    flexGrow: 0,
  },
});

const BundleSend = bundleIcon(SendFilled, SendRegular);
const BundleClipboardTextEdit = bundleIcon(ClipboardTextEditFilled, ClipboardTextEditRegular);
const BundleChatSparkle = bundleIcon(ChatSparkleFilled, ChatSparkleRegular);
const BundleChat = bundleIcon(ChatFilled, ChatRegular);
const BundleHelp = bundleIcon(QuestionCircleFilled, QuestionCircleRegular);
const BundleSettings = bundleIcon(SettingsFilled, SettingsRegular);

const ComposeBox = ({
  addinType,
  properties,
  selectedOptions,
  setSelectedOptions,
  selectedValue,
  setSelectedValue,
  selectedTab,
  setSelectedTab,
  userPromptArea,
  userInput,
  setUserInput,
  handleSendMessage,
  onNewTopic,
  isReplyWindow,
  isComposeReplyWindow,
  onSignout,
  onPostUsage,
  checkedTone,
  setCheckedTone,
  userStyles,
  selectedStyleName,
  onSelectStyle,
  searchChecked,
  onSearchCheckedChange,
  onContextChangeExternal,
  hasSelectionForRewrite,
  plainTextChecked,
  onPlainTextCheckedChange,
  fileContext,
  fileUpload,
  onViewResource,
  sendDisabled,
}) => {
  console.log("isReplyWindow", isReplyWindow);
  console.log("isComposeReplyWindow", isComposeReplyWindow);
  const styles = useStyles();

  const handleInputChange = (_, data) => {
    setUserInput(data.value);
  };

  const handleNewTopic = () => {
    console.log("New topic");
    onNewTopic();
  };

  const handleHelp = () => {
    console.log("Help");
    window.open(
      addinType === "OUTLOOK"
        ? "https://unicli.ai/how-to-use-unicli-for-outlook-add-in/"
        : "https://unicli.ai/how-to-use-unicli-for-word-add-in/",
      "_blank"
    );
    onPostUsage("help", "usage", addinType === "OUTLOOK" ? { source: "outlook" } : { source: "word" });
  };
  const openUnicliPortal = () => {
    console.log("Unicli Portal");
    window.open("https://app.unicli.ai", "_blank");
  };

  useEffect(() => {
    if (!userPromptArea?.current) return;

    const frameId = requestAnimationFrame(() => {
      const el = userPromptArea.current;
      if (!el) return;
      try {
        // Reset height to allow shrink
        el.style.overflow = "hidden";
        el.style.height = "auto";

        const computedStyle = window.getComputedStyle(el);
        const paddingTop = parseInt(computedStyle.paddingTop, 10) || 0;
        const paddingBottom = parseInt(computedStyle.paddingBottom, 10) || 0;
        const borderTop = parseInt(computedStyle.borderTopWidth, 10) || 0;
        const borderBottom = parseInt(computedStyle.borderBottomWidth, 10) || 0;
        const verticalPaddingAndBorder = paddingTop + paddingBottom + borderTop + borderBottom;

        const maxHeight = Math.floor(window.innerHeight * 0.4); // 40% of add-in height
        const contentHeight = el.scrollHeight + verticalPaddingAndBorder;

        if (contentHeight > maxHeight) {
          el.style.height = maxHeight + "px";
          el.style.overflowY = "auto";
        } else {
          el.style.height = contentHeight + "px";
          el.style.overflowY = "hidden";
        }
      } catch (e) {
        // no-op
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [userInput]);

  const [toneMenuOpen, setToneMenuOpen] = useState(false);
  const [styleMenuOpen, setStyleMenuOpen] = useState(false);

  const toggleToneMenu = () => {
    // When opening Tone, ensure Style menu is closed
    setStyleMenuOpen(false);
    setToneMenuOpen((v) => !v);
  };
  const toggleStyleMenu = () => {
    // When opening Style, ensure Tone menu is closed
    setToneMenuOpen(false);
    setStyleMenuOpen((v) => !v);
  };
  const closeMenus = () => {
    setToneMenuOpen(false);
    setStyleMenuOpen(false);
  };
  useEffect(() => {
    const onDocClick = (e) => {
      // Close menus on outside click
      if (toneMenuOpen || styleMenuOpen) {
        setToneMenuOpen(false);
        setStyleMenuOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [toneMenuOpen, styleMenuOpen]);

  return (
    <div id="composeBox" className={addinType == "OUTLOOK" ? styles.composeBox : styles.composeBoxWord}>
      {fileContext?.resourceLoading && (
        <div className={styles.fileResourceLoadingLine} aria-live="polite">
          <Spinner size="tiny" />
          <span>Loading files…</span>
        </div>
      )}
      <div id="contextTabBar" className={addinType == "OUTLOOK" ? styles.contextTabBar : styles.contextTabBarWord}>
        {properties && properties.length > 0 && (
          <ContextBox
            properties={properties}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            onContextChangeExternal={typeof onContextChangeExternal === "function" ? onContextChangeExternal : undefined}
          />
        )}
        <TabList
          id="respondChatTabs"
          className={styles.respondChatTabsCompose}
          selectedValue={selectedTab}
          onTabSelect={(event, data) => {
            setSelectedTab(data.value);
          }}
          tabIndex={0}
        >
          {isReplyWindow ? (
            <>
              <Tab
                id="replyalltab"
                icon={
                  selectedTab === "replyall" ? (
                    <ArrowReplyAllFilled style={{ color: "#881798" }} />
                  ) : (
                    <ArrowReplyAllRegular style={{ color: "#881798" }} />
                  )
                }
                value="replyall"
                className={styles.iconButton}
              >
                <Label className={styles.buttonLabel}>Reply All</Label>
              </Tab>
              <Tab
                id="chattab"
                icon={
                  selectedTab === "chat" ? (
                    <ChatFilled style={{ color: "#357C7C" }} />
                  ) : (
                    <BundleChat style={{ color: "#357C7C" }} />
                  )
                }
                value="chat"
                className={styles.iconButton}
              >
                <Label className={styles.buttonLabel}>Chat</Label>
              </Tab>
            </>
          ) : (
            <>
              <Tab
                id="composetab"
                icon={
                  selectedTab === "new" ? (
                    addinType === "OUTLOOK" ? (
                      <MailFilled style={{ color: "#357C7C" }} />
                    ) : (
                      <DocumentArrowUpFilled style={{ color: "#357C7C" }} />
                    )
                  ) : addinType == "OUTLOOK" ? (
                    <MailRegular style={{ color: "#357C7C" }} />
                  ) : (
                    <DocumentArrowUpRegular style={{ color: "#357C7C" }} />
                  )
                }
                value="new"
                className={styles.iconButton}
              >
                <Label className={styles.buttonLabel}>{addinType === "OUTLOOK" ? "New" : "Insert"}</Label>
              </Tab>
              <Tab
                id="chattab"
                icon={
                  selectedTab === "chat" ? (
                    <ChatFilled style={{ color: "#357C7C" }} />
                  ) : (
                    <BundleChat style={{ color: "#357C7C" }} />
                  )
                }
                value="chat"
                className={styles.iconButton}
                appearance="transparent"
              >
                <Label className={styles.buttonLabel}>Chat</Label>
              </Tab>
            </>
          )}
        </TabList>
      </div>
      {fileContext && (
        <FileContextPicker
          resourceSearchTerm={fileContext.resourceSearchTerm}
          setResourceSearchTerm={fileContext.setResourceSearchTerm}
          filteredResources={fileContext.filteredResources}
          selectedFiles={fileContext.selectedFiles}
          selectFile={fileContext.selectFile}
          resourceLoading={fileContext.resourceLoading}
          resourceError={fileContext.resourceError}
          validationMessage={fileContext.validationMessage}
          loadResources={fileContext.loadResources}
          disabled={false}
          isResourceListEmpty={
            !fileContext.resourceLoading &&
            !fileContext.resourceError &&
            fileContext.availableResources.length === 0
          }
          propertyId={fileContext.propertyId}
          fileUpload={fileUpload}
          onViewResource={onViewResource}
        />
      )}
      {
        <div id="inputArea" className={styles.inputArea}>
          <div id="inputFluent2" className={styles.inputFluent2}>
            <div id="contents" className={styles.contents}>
              {fileContext && (
                <SelectedFileChips
                  selectedFiles={fileContext.selectedFiles}
                  removeFile={fileContext.removeFile}
                />
              )}
              <Textarea
                id="userPromptArea"
                ref={userPromptArea}
                value={userInput}
                onChange={handleInputChange}
                placeholder={
                  selectedTab === "chat"
                    ? "Ask me anything..."
                    : addinType === "OUTLOOK"
                      ? "Provide instructions or leave blank to generate a draft without instruction..."
                      : "Provide instructions to insert text at your cursor. To revise text, highlight it and provide revision instructions..."
                }
                tabIndex={0}
                onKeyDown={(event) =>
                  event.key === "Enter" && event.getModifierState("Alt") ? handleSendMessage() : null
                }
                className={styles.placeholderText}
                textarea={{ className: styles.placeholderTextInput }}
              />
              <div id="actionFrame" className={styles.actionFrame}>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      id="handleHelpButton"
                      className={styles.handleHelp}
                      style={{ minWidth: "unset" }}
                      icon={<BundleHelp style={{ width: "20px", height: "20px", color: "#357C7C" }} />}
                      appearance="transparent"
                      tabIndex={1}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem onClick={handleHelp}>Knowledgebase</MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      id="handleSettingsButton"
                      className={styles.handleSettings}
                      style={{ minWidth: "unset" }}
                      icon={<BundleSettings style={{ width: "20px", height: "20px", color: "#357C7C" }} />}
                      appearance="transparent"
                      tabIndex={1}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem onClick={openUnicliPortal}>Unicli Portal</MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
                <Tooltip
                  content={<UnicliTooltip strongLine="New Topic" mainLine="Clear history" />}
                  positioning="above"
                  relationship="description"
                  withArrow="true"
                  showDelay={2000}
                >
                  <Button
                    id="newTopicButton"
                    onClick={handleNewTopic}
                    className={styles.newTopic}
                    style={{ minWidth: "unset" }}
                    appearance="transparent"
                    tabIndex={1}
                  >
                    <span className={styles.buttonText}>New Topic</span>
                    <BundleChatSparkle style={{ width: "20px", height: "20px", color: "#357C7C" }} />
                  </Button>
                </Tooltip>
                <Tooltip
                  content={SendButtonTooltip(selectedTab, addinType)}
                  positioning="Above"
                  relationship="description"
                  withArrow="true"
                  showDelay={2000}
                >
                  <Button
                    id="submitButton"
                    onClick={handleSendMessage}
                    className={styles.submitButtom}
                    style={{ minWidth: "unset" }}
                    disabled={
                      sendDisabled ||
                      (userInput.trim() === "" &&
                        (selectedTab === "chat" || addinType === "WORD" || (!isReplyWindow && !isComposeReplyWindow)) &&
                        (selectedTab === "chat" || !hasSelectionForRewrite))
                    }
                    tabIndex={0}
                    appearance="transparent"
                  >
                    <span className={styles.buttonText}>
                      {addinType === "OUTLOOK"
                        ? selectedTab === "chat"
                          ? "Send"
                          : hasSelectionForRewrite
                          ? "Rewrite"
                          : "Draft"
                        : selectedTab === "chat"
                        ? "Send"
                        : hasSelectionForRewrite
                        ? "Rewrite"
                        : "Insert"}
                    </span>
                    {selectedTab === "chat" ? (
                      <BundleSend style={{ width: "20px", height: "20px", color: "#357C7C" }} />
                    ) : (
                      <BundleClipboardTextEdit style={{ width: "20px", height: "20px", color: "#357C7C" }} />
                    )}
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      }
      <div id="toneSelector" className={styles.toneSelector}>
        <div className={styles.toneStyleInline}>
          <Label className={styles.toneLabel}>Tone:&nbsp;</Label>
          <div className={styles.popupAnchor}>
            <Label className={styles.toneMenuItem} onClick={(e) => { e.stopPropagation(); toggleToneMenu(); }}>
              {checkedTone}
            </Label>
            {toneMenuOpen && (
              <div className={styles.popupMenu} onClick={(e) => e.stopPropagation()}>
                {["Professional", "Casual", "Humorous"].map((tone) => (
                  <div
                    key={tone}
                    className={styles.popupItem}
                    onClick={() => { setCheckedTone(tone); closeMenus(); }}
                  >
                    {checkedTone === tone ? <b>{tone}</b> : tone}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={styles.toneDivider} style={{ margin: "0 6px" }}></div>
          <Label className={styles.toneLabel}>Style:&nbsp;</Label>
          <div className={styles.popupAnchor}>
            <Label className={styles.toneMenuItem} onClick={(e) => { e.stopPropagation(); toggleStyleMenu(); }}>
              {selectedStyleName || "Default"}
            </Label>
            {styleMenuOpen && (
              <div className={styles.popupMenu} onClick={(e) => e.stopPropagation()}>
                {(Array.isArray(userStyles) && userStyles.length > 0 ? userStyles.map((s) => s.name) : ["Default", "Explanatory", "Concise"]).map((name) => (
                  <div
                    key={name}
                    className={styles.popupItem}
                    onClick={() => { onSelectStyle && onSelectStyle(name); closeMenus(); }}
                  >
                    {selectedStyleName === name ? <b>{name}</b> : name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={styles.searchOptionsAnchor}>
          <div className={styles.searchOptionsGroup}>
            {addinType === "WORD" && selectedTab !== "chat" && typeof plainTextChecked === "boolean" && (
              <div className={styles.searchDocsDiv}>
                <Label className={mergeClasses(styles.searchDocsLabel, "searchDocsLabel")}>Plain text</Label>
                <Checkbox
                  checked={plainTextChecked}
                  onChange={onPlainTextCheckedChange}
                  size="small"
                  className={mergeClasses(styles.searchDocsCheckbox, "searchDocsCheckbox")}
                />
              </div>
            )}
            <div className={styles.searchDocsDiv}>
              <Label className={mergeClasses(styles.searchDocsLabel, "searchDocsLabel")}>Search Docs</Label>
              <Checkbox
                checked={searchChecked}
                onChange={onSearchCheckedChange}
                size="small"
                className={mergeClasses(styles.searchDocsCheckbox, "searchDocsCheckbox")}
              />
            </div>
          </div>
        </div>
      </div>
      <div id="disclaimer" className={styles.disclaimer}>
        <InfoRegular className={styles.infoIcon} />
        <Label className={styles.disclaimerText} size="small">
          Unicli uses AI. Check for mistakes.
        </Label>
        <Link className={styles.signoutLink} size="small" onClick={onSignout} href="#">
          Sign out
        </Link>
      </div>
    </div>
  );
};

ComposeBox.propTypes = {
  addinType: PropTypes.string.isRequired,
  properties: PropTypes.array.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  selectedValue: PropTypes.string.isRequired,
  setSelectedValue: PropTypes.func.isRequired,
  selectedTab: PropTypes.string.isRequired,
  setSelectedTab: PropTypes.func.isRequired,
  userPromptArea: PropTypes.shape({
    current: PropTypes.any,
  }),
  userInput: PropTypes.string.isRequired,
  setUserInput: PropTypes.func.isRequired,
  handleSendMessage: PropTypes.func.isRequired,
  isReplyWindow: PropTypes.bool.isRequired,
  isComposeReplyWindow: PropTypes.bool,
  onNewTopic: PropTypes.func.isRequired,
  onSignout: PropTypes.func.isRequired,
  onPostUsage: PropTypes.func.isRequired,
  checkedTone: PropTypes.string,
  setCheckedTone: PropTypes.func,
  userStyles: PropTypes.array,
  selectedStyleName: PropTypes.string,
  onSelectStyle: PropTypes.func,
  searchChecked: PropTypes.bool.isRequired,
  onSearchCheckedChange: PropTypes.func.isRequired,
  onContextChangeExternal: PropTypes.func,
  hasSelectionForRewrite: PropTypes.bool,
  plainTextChecked: PropTypes.bool,
  onPlainTextCheckedChange: PropTypes.func,
  fileContext: PropTypes.object,
  fileUpload: PropTypes.object,
  onViewResource: PropTypes.func,
  sendDisabled: PropTypes.bool,
};

export default ComposeBox;
